import React, { useState } from "react";
import "./ShopCard.css";
import { Star, Eye, ArrowRight, CheckCircle2, Flame, PhoneCall } from "lucide-react";
import { getPriceForSku } from "../../api/productPriceApi";

const ShopCard = ({ product, onViewDetails, onBuyNow, viewMode = "list" }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  // Process image source
  const imageSource =
    product?.mainImage ||
    product?.images?.[0] ||
    product?.baseImage ||
    "/placeholder-image.jpg";

  const title = product?.title || product?.baseTitle || "Realflame Gas Stove";
  const burnerCount = product?.burnerCount || product?.burners || null;
  const material = product?.material || null;
  const price = product?.price || null;
  const mrp = product?.mrp || null;
  const rating = product?.rating || 4.5;
  const reviews = product?.reviews || product?.ratingCount || 12;
  const description =
    product?.description ||
    product?.subTitle ||
    "High-performance burner stove with durable construction.";

  const isPngProduct = product?.category === "png-stoves" || product?.baseCategory === "png-stoves" || product?.id === "png-stove" || Boolean(product?.sku && (product.sku.startsWith("GT") || product.sku.startsWith("SS")));

  // Calculate dynamic configured pricing from actual product MRP
  const rawMrp = typeof mrp === "number" ? mrp : (typeof price === "number" ? price : (parseFloat(mrp || price) || 0));
  const pricing = isPngProduct && rawMrp > 0 ? getPriceForSku(rawMrp, product?.priceCatalog) : null;
  const effectivePrice = pricing?.effectivePrice ?? rawMrp;
  const isDiscountActive = pricing ? pricing.isDiscountActive : false;
  const savingsAmount = pricing ? pricing.savingsAmount : 0;
  const discountPercentage = pricing ? pricing.discountPercentage : 0;

  const hasBuyNow = isPngProduct && Boolean(product?.flipkartUrl);

  // Extract / derive Glen-style bullet features list
  const bulletPoints = React.useMemo(() => {
    if (product?.specs && product.specs.length > 0) {
      return product.specs.slice(0, 3).map((sp) => `${sp.label}: ${sp.value}`);
    }
    const bullets = [];
    if (material) bullets.push(`Material: ${material}`);
    if (burnerCount) bullets.push(`Burners: ${burnerCount} Brass Burner System`);
    bullets.push("Strong & sturdy pan supports for heavy cooking");
    return bullets;
  }, [product, material, burnerCount]);

  const renderStars = (ratingVal) => {
    const fullStars = Math.floor(ratingVal);
    const hasHalfStar = ratingVal % 1 >= 0.4;
    const stars = [];

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <Star key={i} size={14} className="star-icon filled" fill="#FF7A00" color="#FF7A00" />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <Star key={i} size={14} className="star-icon half" fill="url(#halfStarGradient)" color="#FF7A00" />
        );
      } else {
        stars.push(
          <Star key={i} size={14} className="star-icon empty" fill="#E5E7EB" color="#D1D5DB" />
        );
      }
    }
    return stars;
  };

  // GLEN STYLE HORIZONTAL LIST CARD VIEW
  if (viewMode === "list") {
    return (
      <div className="rf-glen-list-card">
        {/* SVG Gradient definition for half star */}
        <svg width="0" height="0" className="absolute hidden">
          <defs>
            <linearGradient id="halfStarGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="50%" stopColor="#FF7A00" />
              <stop offset="50%" stopColor="#E5E7EB" />
            </linearGradient>
          </defs>
        </svg>

        {/* Left: Image Container */}
        <div className="rf-glen-img-wrapper" onClick={onViewDetails}>
          <img
            src={imageSource}
            alt={title}
            loading="lazy"
            onLoad={handleImageLoad}
            className={`rf-glen-img ${imageLoaded ? "loaded" : "loading"}`}
          />
        </div>

        {/* Center: Title, Ratings, Bullet Points & View Details Link */}
        <div className="rf-glen-center-info">
          <h3 className="rf-glen-title" onClick={onViewDetails} title={title}>
            {title}
          </h3>

          <div className="rf-glen-rating-row">
            <div className="rf-stars-container">{renderStars(rating)}</div>
            <span className="rf-glen-reviews-text">{reviews} Reviews</span>
          </div>

          <ul className="rf-glen-bullets-list">
            {bulletPoints.map((bullet, idx) => (
              <li key={idx}>– {bullet}</li>
            ))}
          </ul>

          <button
            type="button"
            className="rf-glen-view-details-link"
            onClick={onViewDetails}
          >
            View full details
          </button>
        </div>

        {/* Right: Price, MRP & CTA Button */}
        <div className="rf-glen-right-action">
          {isPngProduct && (mrp || typeof price === "number") ? (
            <div className="rf-glen-pricing-box flex flex-col gap-1 items-end">
              <div className="rf-glen-price-row flex items-baseline gap-2">
                <span className="rf-glen-price text-xl font-bold text-orange-600">
                  ₹{Number(effectivePrice).toLocaleString('en-IN')}
                </span>
                {isDiscountActive && savingsAmount > 0 ? (
                  <>
                    <span className="rf-glen-mrp line-through text-xs text-gray-400">
                      MRP ₹{Number(rawMrp).toLocaleString('en-IN')}
                    </span>
                    <span className="badge bg-danger text-white font-semibold text-xs px-1.5 py-0.5 rounded">
                      {discountPercentage}% OFF
                    </span>
                  </>
                ) : (
                  <span className="text-xs text-gray-500">Standard Price</span>
                )}
              </div>
            </div>
          ) : (
            <div className="rf-glen-custom-price">
              Contact for Quote
            </div>
          )}

          <button
            type="button"
            className={`rf-glen-buy-btn ${!hasBuyNow ? "contact-btn" : ""}`}
            onClick={onBuyNow}
          >
            {hasBuyNow ? (
              <>BUY NOW <ArrowRight size={15} /></>
            ) : (
              <>CONTACT US <PhoneCall size={14} /></>
            )}
          </button>
        </div>
      </div>
    );
  }

  // STANDARD GRID CARD VIEW
  return (
    <div className="rf-premium-card group">
      <svg width="0" height="0" className="absolute hidden">
        <defs>
          <linearGradient id="halfStarGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="50%" stopColor="#FF7A00" />
            <stop offset="50%" stopColor="#E5E7EB" />
          </linearGradient>
        </defs>
      </svg>

      <div className="rf-card-image-wrapper" onClick={onViewDetails}>
        {!imageLoaded && <div className="rf-shimmer-placeholder" />}
        <img
          src={imageSource}
          alt={title}
          loading="lazy"
          onLoad={handleImageLoad}
          className={`rf-card-image ${imageLoaded ? "loaded" : "loading"}`}
        />
        <div className="rf-image-overlay">
          <button
            type="button"
            className="rf-quick-view-hover-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (onViewDetails) onViewDetails();
            }}
          >
            <Eye size={16} /> Quick View
          </button>
        </div>
      </div>

      <div className="rf-card-content">
        <div className="rf-card-chips">
          {material && (
            <span className="rf-chip rf-chip-material">
              <CheckCircle2 size={12} /> {material}
            </span>
          )}
          {burnerCount && (
            <span className="rf-chip rf-chip-burner">
              <Flame size={12} /> {burnerCount} {burnerCount === 1 ? "Burner" : "Burners"}
            </span>
          )}
        </div>

        <h3 className="rf-card-title" onClick={onViewDetails} title={title}>
          {title}
        </h3>

        <p className="rf-card-description">{description}</p>

        <div className="rf-card-rating-row">
          <div className="rf-stars-container">{renderStars(rating)}</div>
          <span className="rf-rating-score">{rating}</span>
          <span className="rf-rating-count">({reviews} Ratings)</span>
        </div>

        {isPngProduct && (mrp || typeof price === "number") ? (
          <div className="rf-card-price-row flex flex-col gap-1">
            <div className="rf-price-box flex items-baseline gap-2">
              <span className="rf-price-amount text-xl font-bold text-orange-600">
                ₹{Number(effectivePrice).toLocaleString('en-IN')}
              </span>
              {isDiscountActive && savingsAmount > 0 ? (
                <span className="line-through text-xs text-gray-400">
                  MRP ₹{Number(rawMrp).toLocaleString('en-IN')}
                </span>
              ) : null}
            </div>
            {isDiscountActive && savingsAmount > 0 ? (
              <span className="text-xs font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded w-max border border-green-200">
                ⚡ Save ₹{Number(savingsAmount).toLocaleString('en-IN')} ({discountPercentage}% OFF)
              </span>
            ) : null}
          </div>
        ) : (
          <div className="rf-card-price-row text-sm text-gray-500 font-medium">
            Contact for Quote
          </div>
        )}

        <div className="rf-card-actions">
          <button
            type="button"
            className="rf-btn-view-details"
            onClick={onViewDetails}
          >
            VIEW DETAILS
          </button>
          <button
            type="button"
            className={`rf-btn-buy-now ${!hasBuyNow ? "contact-btn" : ""}`}
            onClick={onBuyNow}
          >
            {hasBuyNow ? (
              <>BUY NOW <ArrowRight size={15} className="rf-arrow-icon" /></>
            ) : (
              <>CONTACT US <PhoneCall size={14} className="rf-arrow-icon" /></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShopCard;